import {
  createContext,
  memo,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { PromiseQueue } from '~/classes/PromiseQueue';
import { SearchTranscriptFetchQueueProvider } from '~/providers/SearchTranscriptFetchQueueProvider';
import { type ClipTranscriptCache, type TimeRange } from '~/swr-hooks/clips/transcript/types';
import { isDevOrTestStage } from '~/swr-hooks/utils';

type QueueRangeFetchParams = TimeRange & {
  clipId: string;
  fn: () => Promise<ClipTranscriptCache>;
};

type ClipTranscriptFetchQueueContextType = {
  fetchQueue: RefObject<PromiseQueue<ClipTranscriptCache> | undefined>;
  queueRangeFetch: (params: QueueRangeFetchParams) => Promise<ClipTranscriptCache> | undefined;
};

const defaultValue: ClipTranscriptFetchQueueContextType = {
  fetchQueue: { current: undefined },
  queueRangeFetch: () => undefined,
};

export const ClipTranscriptFetchQueueContext = createContext<ClipTranscriptFetchQueueContextType>(defaultValue);

export type ClipTranscriptFetchQueueProviderProps = {
  children: ReactNode;
};

export const ClipTranscriptFetchQueueProvider = memo(({ children }: ClipTranscriptFetchQueueProviderProps) => {
  const fetchQueue = useRef<PromiseQueue<ClipTranscriptCache>>();

  useEffect(() => {
    fetchQueue.current = new PromiseQueue();

    return () => {
      fetchQueue.current?.cancelAll();
    };
  }, []);

  const queueRangeFetch = useCallback(({ clipId, startTimeMs, endTimeMs, fn }: QueueRangeFetchParams) => {
    return fetchQueue.current?.run(`${clipId}-${startTimeMs}-${endTimeMs}`, fn);
  }, []);

  const value = useMemo<ClipTranscriptFetchQueueContextType>(
    () => ({ fetchQueue, queueRangeFetch }),
    [queueRangeFetch],
  );

  return (
    <ClipTranscriptFetchQueueContext.Provider value={value}>
      <SearchTranscriptFetchQueueProvider>{children}</SearchTranscriptFetchQueueProvider>
    </ClipTranscriptFetchQueueContext.Provider>
  );
});

ClipTranscriptFetchQueueProvider.displayName = 'ClipTranscriptFetchQueueProvider';

export const useClipTranscriptFetchQueueContext = () => {
  const context = useContext(ClipTranscriptFetchQueueContext);

  if (context === defaultValue) {
    const error = 'ClipTranscriptFetchQueueContext used outside of ClipTranscriptFetchQueueProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
